import { useEffect } from "react";
import { BRAND } from "../data/siteData";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export default function SEO({ title, description, path = "/", ogImage, schemas = [], noindex = false }) {
  useEffect(() => {
    const url = `${BRAND.domain}${path}`;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:site_name", BRAND.name);
    if (ogImage) setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (ogImage) setMeta("name", "twitter:image", ogImage);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    document.head.querySelectorAll("script[data-vx-jsonld]").forEach((s) => s.remove());
    schemas.filter(Boolean).forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-vx-jsonld", "1");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, [title, description, path, ogImage, noindex, schemas]);
  return null;
}

export const localBusinessSchema = (areaServed) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: BRAND.name,
  description: "Premium TRI-BOND™ polyaspartic garage floor coating installed in one day with a lifetime limited warranty. Serving the Phoenix Metro Area.",
  url: BRAND.domain,
  telephone: "+1-480-351-6577",
  email: BRAND.email,
  priceRange: "$$",
  address: { "@type": "PostalAddress", addressLocality: "Phoenix", addressRegion: "AZ", addressCountry: "US" },
  areaServed: (areaServed || ["Phoenix Metro Area"]).map((a) => ({ "@type": "City", name: a })),
  aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "47" },
});

export const serviceSchema = (name, areaName) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@type": "LocalBusiness", name: BRAND.name, telephone: "+1-480-351-6577" },
  areaServed: { "@type": "City", name: areaName || "Phoenix" },
  description: "One-day polyaspartic garage floor coating with lifetime limited warranty.",
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${BRAND.domain}${it.path}`,
  })),
});

export const reviewsSchema = (testimonials) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: BRAND.name,
  url: BRAND.domain,
  telephone: "+1-480-351-6577",
  address: { "@type": "PostalAddress", addressLocality: "Phoenix", addressRegion: "AZ", addressCountry: "US" },
  aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: String(testimonials.length) },
  review: testimonials.map((t) => ({
    "@type": "Review",
    author: { "@type": "Person", name: t.name },
    reviewRating: { "@type": "Rating", ratingValue: String(t.stars) },
    reviewBody: t.text,
  })),
});

export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.meta_description,
  image: post.image,
  author: { "@type": "Organization", name: BRAND.name },
  publisher: { "@type": "Organization", name: BRAND.name, url: BRAND.domain },
  datePublished: post.created_at,
  mainEntityOfPage: `${BRAND.domain}/blog/${post.slug}`,
});
